import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useRef } from 'react';
import { Box, TextField, Stack, InputLabel, Typography, Tooltip, IconButton, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  CreateTaskTemplateBody,
  TaskTemplate,
} from '../../../../../shared/types/Task';
import { showSnackbar } from '../../../../../frontend/utils/snackbar';
import { useDispatch } from 'react-redux/es/hooks/useDispatch';

const textFieldSx = {
  color: '#222222',
  '& > p': {
    margin: '5px 0px',
    color: '#646669',
    fontSize: typographyTokens.fontSizes.xs,
  },
};

interface FirstStepProps {
  taskid:number
  task: CreateTaskTemplateBody | TaskTemplate;
  description: string;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  templateTasksShortNameList: string[];
  setIsAdminShortNameValid:React.Dispatch<React.SetStateAction<boolean>>
}

const FirstStep = ({
  taskid,
  task,
  handleChange,
  description,
  setDescription,
  templateTasksShortNameList,
  setIsAdminShortNameValid
}: FirstStepProps) => {

  const timeoutRef = useRef<number | undefined>(undefined);
  const dispatch = useDispatch();
  const theme = useTheme();

  const handleTaskShortNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;    
    handleChange(e);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      if (!value) {
        setIsAdminShortNameValid(true);
        return;
      }

      const shortNames = templateTasksShortNameList?.map(shortName => shortName.toLowerCase()) || [];
      const taskShortName = value.toLowerCase();
      const isDuplicate = shortNames.includes(taskShortName);

      setIsAdminShortNameValid(!isDuplicate);

      if (isDuplicate) {
        showSnackbar(
          dispatch,
          { errorMessage: `The task name '${value}' is already in use. Please choose a different name.` },
          'error',
          7000
        );
      }

    }, 500);
  };

  return (
    <Box>
      <Stack
        display="flex"
        direction="column"
        alignItems="left"
        spacing={{ xs: 2, md: 4 }}
        sx={{
          p: { xs: 2, md: 4 },
        }}
      >
        {taskid >0  &&
  <Box>
          <Typography
              component="span"
              sx={{
                fontSize: typographyTokens.fontSizes.body,
                fontWeight: typographyTokens.fontWeights.bold,
              }}
            >
              Task ID : {taskid}
            </Typography>
        </Box>
        }
        
        <Box>
          <InputLabel
            sx={{
              color:  theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222',
            }}
            htmlFor="task-name"
          >
            Task Name
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="name"
            name="name"
            fullWidth
            data-testid="task-name"
            value={task.name || ''}
            placeholder="Ex. Please have vendor complete the Cloud Security Requirements Mat..."
            helperText="This should be the actual task name that will display on an SEP's Task List."
            onChange={(e) => handleChange(e)}
          />
        </Box>
        <Box>
          <InputLabel
            sx={{
              color:  theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222',
            }}
            htmlFor="task-name"
          >
            Task Assigment Name
          </InputLabel>
          <TextField
            sx={textFieldSx}
            size="small"
            id="shortName"
            name="shortName"
            data-testid="short-name"
            fullWidth
            value={task.shortName || ''}
            placeholder="Ex. Please have vendor complete the Cloud Security Requirements Mat.."
            helperText="This name appears in the Task Assigner options on the Review tab of an SEP's Overview"
            onChange={(e) => handleTaskShortNameChange(e)}
          />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
            <InputLabel
              sx={{ color:  theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222', mb: 0 }}
              htmlFor="task-assignment-name"
            >
              Task Description
            </InputLabel>
            <Tooltip
              title={
                <span style={{ fontSize: typographyTokens.fontSizes.lg, lineHeight: '1.4' }}>
                  <b>How to Style and Save a Text Description</b><br/><br />
                  1. Write your text description in Microsoft Word.<br />
                  2. Save the file as “Web Page, Filtered (*.htm, *.html)” or “Web Page (*.htm, *.html)”.<br />
                  3. Open the saved file using Notepad.<br />
                  4. Copy all the HTML content from Notepad.<br />
                  5. Paste the HTML content into the textarea box provided here and save your changes.
                </span>
              }
              placement="top"
              arrow
            >
              <IconButton size="small">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <textarea
            style={{ height: '40vh', width: '100%', resize: 'vertical', borderColor: '#c4c4c4', borderRadius: '6px', padding: '10px', backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#ffffff', color: theme.palette.mode === 'dark' ? theme.palette.white.main : '#222222' }}
            aria-label="task-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Ex. We need to understand the scope to determine which teams will need to review this request. Please select all that apply."
          />
          <Typography
            sx={{
              margin: '5px 0px',
              color: '#646669',
              fontSize: typographyTokens.fontSizes.xs,
            }}
          >
            Keep it 2-3 sentences. Briefly describe why this screen is here and
            how it helps the process.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 5 }}>
          <Box>
            <InputLabel
              sx={{
                color:  theme.palette.mode === 'dark' ? theme.palette.white.main :'#222222',
              }}
            >
              Group Name
            </InputLabel>
            <TextField
              sx={textFieldSx}
              size="small"
              id="groupName"
              name="groupName"
              style={{ width: 250 }}
              data-testid="task-name"
              value={task?.groupName || ''} 
              onChange={(e) => handleChange(e)}
              placeholder="Enter task Group name"
              helperText="Use to create multi-task selection."
            />
          </Box>

          <Box>
            <InputLabel
              sx={{
                color:  theme.palette.mode === 'dark' ? theme.palette.white.main : "#222222",
              }}
            >
              Group Number
            </InputLabel>
            <TextField
              sx={textFieldSx}
              size="small"
              id="groupId"
              name="groupId"
              style={{ width: 250 }}
              data-testid="task-id"
              value={task?.groupId || ''}
              placeholder="Enter task Group id"
              helperText="Use to position task on department review tab."
              onChange={(e) => handleChange(e)}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default FirstStep;
